import type { UseMutationResult } from "react-query";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { paywallSubscriptionsApi } from "@/react/api/paywallSubscriptionsApi";
import type { ApiError } from "@/react/config/CustomErrors";
import type { CommunityMemberSubscription } from "@/react/types/CommunityMemberSubscription";
import { useToast } from "@circle-react-uikit/ToastV2";

const { resumeSubscription } = paywallSubscriptionsApi;

interface ResumeSubscriptionCallbacks {
  onSuccess?: () => void;
}

interface UseResumePaywallSubscriptionApiProps {
  callback?: ResumeSubscriptionCallbacks;
}

interface UseResumePaywallSubscriptionApiReturn {
  resumeMutation: UseMutationResult<
    CommunityMemberSubscription,
    ApiError,
    string
  >;
}

export const useResumePaywallSubscriptionApi = ({
  callback,
}: UseResumePaywallSubscriptionApiProps): UseResumePaywallSubscriptionApiReturn => {
  const { success, error } = useToast();
  const defaultErrorMessage = t(
    "settings.paywalls_admin_dashboard.subscriptions.alerts.errors.action_failed",
  );

  const resumeMutation = useMutation({
    mutationFn: (subscriptionProcessorId: string) =>
      resumeSubscription({
        subscriptionProcessorId,
      }),
    onSuccess: () => {
      callback?.onSuccess?.();
      success(
        t(
          "settings.paywalls_admin_dashboard.subscriptions.alerts.success.resume_subscription",
        ),
      );
    },
    onError: (updateError: ApiError) => {
      console.error(updateError.errorDetails);
      const errorMessage =
        typeof updateError.errorDetails === "string"
          ? updateError.errorDetails
          : defaultErrorMessage;
      error(errorMessage, {
        duration: "long",
      });
    },
  });

  return {
    resumeMutation,
  };
};
